import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Leaf, LogOut, ShoppingBag, Package, User } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useCart } from "../CartContext";
import { useNurserySettings } from "../SettingsContext";
import { Toaster } from "./ui/sonner";
import MobileNavSheet from "./MobileNavSheet";

const nav = [
  { to: "/catalog", label: "Catalog", icon: ShoppingBag, testid: "buyer-nav-catalog" },
  { to: "/orders", label: "My orders", icon: Package, testid: "buyer-nav-orders" },
  { to: "/profile", label: "Profile", icon: User, testid: "buyer-nav-profile" },
];

const BrandBlock = () => {
  const { nurseryName, logoUrl } = useNurserySettings();
  return (
    <>
      <span
        className="w-8 h-8 rounded-md bg-[#2D6A4F] text-white grid place-items-center overflow-hidden shrink-0"
        data-testid="buyer-brand-logo"
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" className="w-full h-full object-contain" />
        ) : (
          <Leaf size={16} />
        )}
      </span>
      <div className="db-h">
        <div className="text-sm font-bold leading-tight truncate max-w-[180px]" data-testid="buyer-brand-name">
          {nurseryName}
        </div>
        <div className="text-[10px] text-[#5C665A] leading-tight uppercase tracking-wide">Wholesale</div>
      </div>
    </>
  );
};

export default function BuyerLayout() {
  const { user, logout } = useAuth();
  const { totalUnits, cart } = useCart();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };

  // Mobile drawer footer: signed-in email + logout
  const drawerFooter = (
    <>
      <div className="text-xs text-[#5C665A] mb-2">
        <div>Signed in as</div>
        <div className="text-[#1A1D1A] font-medium truncate">{user?.email}</div>
      </div>
      <button
        data-testid="m-buyer-logout"
        onClick={doLogout}
        className="w-full inline-flex items-center gap-2 justify-center min-h-[44px] rounded-md border border-[#E2E4E0] text-sm text-[#1A1D1A] hover:bg-[#F9F9F8] transition-colors active:scale-95"
      >
        <LogOut size={14} /> Log out
      </button>
    </>
  );

  // Include cart as a drawer item on mobile too (redundant with the top-bar badge but expected in menus)
  const mobileNavItems = [
    ...nav,
    { to: "/cart", label: `Cart${totalUnits > 0 ? ` (${totalUnits})` : ""}`, icon: ShoppingBag, testid: "buyer-nav-cart" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F8]" data-testid="buyer-layout">
      <header className="sticky top-0 z-20 bg-white border-b border-[#E2E4E0]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-4">
          {/* Mobile: hamburger */}
          <MobileNavSheet
            items={mobileNavItems}
            header={<BrandBlock />}
            footer={drawerFooter}
            triggerTestid="buyer-mobile-nav-trigger"
          />

          <Link to="/catalog" className="flex items-center gap-2" data-testid="buyer-brand">
            <BrandBlock />
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {nav.map((n) => (
              <NavLink
                key={n.to} to={n.to} data-testid={n.testid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm transition-colors ${
                    isActive ? "bg-[#E9F5EC] text-[#1B4332] font-medium" : "text-[#1A1D1A] hover:bg-[#F9F9F8]"
                  }`
                }
              >
                <n.icon size={14} /> {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            {/* Cart link — icon-only on very small screens, keeps badge visible */}
            <Link
              to="/cart"
              data-testid="buyer-cart-link"
              className="inline-flex items-center gap-1.5 px-2 sm:px-3 min-h-[44px] sm:h-9 sm:min-h-0 rounded-md text-sm border border-[#E2E4E0] bg-white hover:bg-[#F9F9F8] transition-colors relative"
              aria-label="Cart"
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">Cart</span>
              {totalUnits > 0 && (
                <span
                  data-testid="buyer-cart-badge"
                  className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-[#2D6A4F] text-white text-[11px] px-1.5 db-mono
                    sm:static absolute -top-1.5 -right-1.5 sm:-top-0 sm:-right-0"
                >
                  {totalUnits}
                </span>
              )}
            </Link>

            <div className="hidden lg:block text-right leading-tight">
              <div className="text-xs text-[#5C665A]">Signed in</div>
              <div className="text-xs font-medium" data-testid="buyer-header-email">{user?.email}</div>
            </div>
            <button
              data-testid="buyer-logout"
              onClick={doLogout}
              className="hidden md:inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm border border-[#E2E4E0] hover:bg-[#F9F9F8] transition-colors"
              title="Log out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <footer className="border-t border-[#E2E4E0] bg-white text-xs text-[#5C665A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div>All prices in EUR, VAT-exclusive. Cart subtotal excludes shipping.</div>
          <div className="flex items-center gap-4">
            {cart?.buyer_discount_percent > 0 && <div>Your personal discount: {cart.buyer_discount_percent}%</div>}
            <Link to="/terms" className="underline underline-offset-2 hover:no-underline" data-testid="footer-terms-link">
              Terms and Conditions
            </Link>
          </div>
        </div>
      </footer>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
